import React, { useState, useEffect, memo } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GMap from '..'
import { TYPES } from './const'
import { getInfoWindowContent } from './util'
import { pushApiError } from 'Reducers/Errors/actions'

const DisplayMap = memo(
  ({
    onMapLoad,
    markers,
    polygons,
    paths,
    center,
    heatMapData: dataForHeatMap,
    pushApiError,
  }) => {
    const [google, setScript] = useState(null)
    const [map, setMap] = useState(null)
    const [figures, setFigures] = useState([])
    const [heatmaps, setHeatmaps] = useState([])

    useEffect(() => {
      if (!google || !map) return
      onMapLoad(google, { map, markers, paths, polygons })
    }, [map, google])

    useEffect(() => {
      if (!google || !map) return
      renderItems()
    }, [map, google, markers, paths, polygons, dataForHeatMap])

    useEffect(() => {
      if (map) {
        map.setCenter(center)
      }
    }, [center])

    const renderItems = () => {
      eraseOldHeatmaps()
      eraseOldFigures()

      renderPaths(paths)
      renderMarkers(markers)
      renderHeatLayer(dataForHeatMap)
      renderPolygons(polygons)
    }

    const renderPaths = paths => paths && paths.forEach(renderPath)
    const renderMarkers = markers => markers && markers.forEach(renderMarker)
    const renderPolygons = polygons =>
      polygons && polygons.forEach(renderPolygon)

    const renderPath = polyline =>
      polyline &&
      saveFigure({
        newFigure: new google.maps.Polyline({
          objectId:
            polyline.customProperties && polyline.customProperties.objectId,
          map: map,
          path: polyline.path,
          geodesic: true,
          strokeOpacity: 1.0,
          strokeWeight: 2,
          ...polyline.options,
        }),
        rawObj: polyline,
        type: TYPES.POLYLINE,
      })
    const renderHeatLayer = data => {
      if (!data) return
      const heatMapData = data.reduce((acc, item) => {
        if (
          item &&
          typeof item.latitude === 'number' &&
          !isNaN(item.latitude) &&
          typeof item.longitude === 'number' &&
          !isNaN(item.longitude) &&
          typeof item.weight === 'number' &&
          !isNaN(item.weight)
        ) {
          acc.push({
            location: new google.maps.LatLng(item.latitude, item.longitude),
            weight: item.weight,
          })
        }
        return acc
      }, [])

      if (heatMapData.length === 0) return

      var heatMap = new google.maps.visualization.HeatmapLayer({
        data: heatMapData,
      })
      heatMap.set('radius', 40)
      heatMap.setMap(map)
      setHeatmaps([...heatmaps, heatMap])
    }
    const renderMarker = marker =>
      marker &&
      saveFigure({
        newFigure: new google.maps.Marker({
          objectId: marker.customProperties && marker.customProperties.objectId,
          position: marker.position,
          map: map,
          ...marker.options,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            ...(marker.options.icon || {}),
          },
        }),
        rawObj: marker,
        type: TYPES.MARKER,
      })

    const renderPolygon = polygon =>
      polygon &&
      saveFigure({
        newFigure: new google.maps.Polygon({
          objectId:
            polygon.customProperties && polygon.customProperties.objectId,
          map: map,
          paths: polygon.path,
          clickable: false,
          ...polygon.options,
        }),
        rawObj: polygon,
        type: TYPES.POLYGON,
      })

    const saveFigure = ({ newFigure, rawObj, type }) => {
      const { infoWindowSettings } = rawObj.options

      newFigure.addListener('click', ({ latLng }) => {
        // do not open the same info window multiple times
        if (newFigure.infoWindow && newFigure.infoWindow.getMap()) {
          return
        }
        const infoWindow = new google.maps.InfoWindow({
          ...infoWindowSettings,
          objectId: newFigure.objectId,
          position: latLng,
          content: getInfoWindowContent({
            type,
            clickCoords: latLng.toJSON(),
            path: rawObj.path,
            fallbackContent: infoWindowSettings.content,
          }),
        })

        newFigure.infoWindow = infoWindow

        closeOpenedInfoWindows()
        infoWindow.open(map)
      })

      setFigures(existingFigures => {
        const newFigures = existingFigures.filter(f => {
          if (
            newFigure.objectId &&
            f.objectId &&
            newFigure.objectId === f.objectId &&
            newFigure.type === f.type
          ) {
            // update opened infoWindow
            if (f.infoWindow && f.infoWindow.getMap()) {
              const infoWindow = new google.maps.InfoWindow({
                ...infoWindowSettings,
                objectId: newFigure.objectId,
                position: f.infoWindow.position,
                content: getInfoWindowContent({
                  type,
                  clickCoords: f.infoWindow.position.toJSON(),
                  path: rawObj.path,
                  fallbackContent: infoWindowSettings.content,
                }),
              })

              f.infoWindow.close()

              newFigure.infoWindow = infoWindow
              infoWindow.open(map)
            }

            return false
          }

          return true
        })

        return [...newFigures, newFigure]
      })
    }

    const closeOpenedInfoWindows = () => {
      setFigures(existingFigures => {
        existingFigures.forEach(f => {
          // close all opened infoWindows
          if (f.infoWindow && f.infoWindow.getMap()) {
            f.infoWindow.close()
          }
        })

        return existingFigures
      })
    }

    const onInit = (map, google) => {
      setMap(map)
      setScript(google)
      google.maps.event.addListenerOnce(map, 'tilesloaded', function() {
        google.maps.event.clearListeners(map, 'tilesloaded')
      })
    }

    const eraseOldFigures = () => figures.forEach(figure => figure.setMap(null))
    const eraseOldHeatmaps = () =>
      heatmaps.forEach(heatmap => heatmap.setMap(null))

    return (
      <GMap
        onInit={onInit}
        mapSettings={{
          zoom: 12,
          center,
        }}
      />
    )
  },
)

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      pushApiError,
    },
    dispatch,
  )

export default connect(
  null,
  mapDispatchToProps,
)(DisplayMap)
